/* eslint-disable react/require-default-props */
import React, { useState, useCallback } from 'react';
import { Button, PhotoCarousel } from '@ui';
import { translate } from '@utils/i18n';
import { DeficienciesTable } from './components/DeficienciesTable';
import { DeficienciesGrid } from './components/DeficienciesGrid';
import { DeficienciesFiltersBar } from './components/DeficienciesFiltersBar';
import { DeficienciesMassActionsToolbar } from './MassActions';
import { DEFAULT_LINKED_COLUMN_KEYS, DEFAULT_CANDIDATE_COLUMN_KEYS } from './groupBy';
import './DeficienciesListStyles.scss';
const NS = 'features.proposals.deficiencies_step';
const t = (key) => translate(key, { namespace: NS });
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
/**
 * Shared deficiency list: table + grid, FilterBar (via Table filterRow in table view),
 * Configure with group-by, view-toggle, session-only column visibility.
 *
 * Table view chrome (top to bottom, decision 15 / 19):
 *   1. View toggle — outside Table, first child.
 *   2. Table chrome: filter row (FilterBar left, Tag + Configure right)
 *                    → mass-action toolbar (Selection + actions)
 *                    → accordion / flat body.
 *
 * Grid view chrome (top to bottom):
 *   1. View toggle (same element, outside Table).
 *   2. Sibling DeficienciesFiltersBar (full width, no Configure).
 *   3. Sibling DeficienciesMassActionsToolbar (Selection + action buttons).
 *   4. DeficienciesGrid (flat, no grouping, no Configure).
 *
 * groupBy and visibleColumnKeys survive view toggle.
 */
export function DeficienciesList({ mode, rows, isLoading = false, isError = false, proposalId, selectedItems, onSelectionChange, onSelectAll, filters, onFiltersChange, groupBy, onGroupByChange, onOpenDetail, onPhotoClick, onToggleIncludePhotos, onRemove, assetsForSelect = [], severityOptions = [] }) {
    // View toggle — local state only
    const [view, setView] = useState('table');
    // Session-only column visibility (decision 18). Initialized from mode-specific
    // defaults; not persisted to localStorage.
    const defaultColumnKeys = mode === 'linked' ? DEFAULT_LINKED_COLUMN_KEYS : DEFAULT_CANDIDATE_COLUMN_KEYS;
    const [visibleColumnKeys, setVisibleColumnKeys] = useState(defaultColumnKeys);
    const switchToTable = useCallback(() => setView('table'), []);
    const switchToGrid = useCallback(() => setView('grid'), []);
    const handleClearSelection = useCallback(() => {
        onSelectAll(false);
    }, [onSelectAll]);
    // First selected card anchor for the grid floating toolbar (task-13).
    const [firstSelectedCardAnchor, setFirstSelectedCardAnchor] = useState(null);
    const handleFirstSelectedCardRef = useCallback((el) => {
        setFirstSelectedCardAnchor(el);
    }, []);
    const [carouselSlides, setCarouselSlides] = useState([]);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [carouselOpen, setCarouselOpen] = useState(false);
    const handlePhotoClick = useCallback((id) => {
        const row = rows.find((item) => item.id === id);
        if (!row || row.photos.length === 0) {
            onPhotoClick === null || onPhotoClick === void 0 ? void 0 : onPhotoClick(id);
            return;
        }
        setCarouselSlides(row.photos.map((photo) => ({ src: photo.src })));
        setCarouselIndex(0);
        setCarouselOpen(true);
    }, [onPhotoClick, rows]);
    const handleCarouselClose = useCallback(() => {
        setCarouselOpen(false);
    }, []);
    return (React.createElement("div", { className: "deficiencies-list" },
        React.createElement("div", { className: "deficiencies-list__view-toggle", role: "group", "aria-label": t('view_toggle_label') },
            React.createElement("span", { className: "deficiencies-list__view-label" }, t('view_label')),
            React.createElement(Button, { type: "button", variant: "default", className: view === 'table'
                    ? 'deficiencies-list__view-button deficiencies-list__view-button--selected'
                    : 'deficiencies-list__view-button', onClick: switchToTable, "aria-label": t('toggle_table_view'), "aria-pressed": view === 'table' },
                React.createElement("i", { className: "fa-light fa-table-rows", "aria-hidden": "true" })),
            React.createElement(Button, { type: "button", variant: "default", className: view === 'grid'
                    ? 'deficiencies-list__view-button deficiencies-list__view-button--selected'
                    : 'deficiencies-list__view-button', onClick: switchToGrid, "aria-label": t('toggle_grid_view'), "aria-pressed": view === 'grid' },
                React.createElement("i", { className: "fa-light fa-grid-2", "aria-hidden": "true" }))),
        view === 'table' ? (
        /* Table view: Table owns FilterBar + Configure (same row), mass actions
           (next row), and accordion/flat body. No sibling FilterBar, MassActions,
           or GroupByMenu. */
        React.createElement(DeficienciesTable, { mode: mode, rows: rows, isLoading: isLoading, isError: isError, selectedItems: selectedItems, onSelectionChange: onSelectionChange, onSelectAll: onSelectAll, visibleColumnKeys: visibleColumnKeys, defaultColumnKeys: defaultColumnKeys, onApplyColumnVisibility: setVisibleColumnKeys, groupBy: groupBy, onGroupByChange: onGroupByChange, filters: filters, onFiltersChange: onFiltersChange, assetsForSelect: assetsForSelect, severityOptions: severityOptions, proposalId: proposalId, onClearSelection: handleClearSelection, onToggleIncludePhotos: onToggleIncludePhotos, onRemove: onRemove, onOpenDetail: onOpenDetail, onPhotoClick: handlePhotoClick })) : (
        /* Grid view: sibling FilterBar (no Configure), sibling Toolbar, flat Grid.
           groupBy and visibleColumnKeys survive the toggle. */
        React.createElement(React.Fragment, null,
            React.createElement(DeficienciesFiltersBar, { filters: filters, onFiltersChange: onFiltersChange, assetsForSelect: assetsForSelect, severityOptions: severityOptions }),
            proposalId !== undefined && (React.createElement(DeficienciesMassActionsToolbar, { mode: mode, proposalId: proposalId, selectedItems: selectedItems, totalCount: rows.length, onSelectAll: onSelectAll, onClearSelection: handleClearSelection, anchorElement: firstSelectedCardAnchor !== null && firstSelectedCardAnchor !== void 0 ? firstSelectedCardAnchor : undefined, contentWidth: "100%" })),
            React.createElement(DeficienciesGrid, { mode: mode, rows: rows, selectedItems: selectedItems, onSelectionChange: onSelectionChange, onOpenDetail: onOpenDetail, onPhotoClick: handlePhotoClick, onRemove: onRemove, onToggleIncludePhotos: onToggleIncludePhotos, onFirstSelectedCardRef: handleFirstSelectedCardRef }))),
        React.createElement(PhotoCarousel, { open: carouselOpen, index: carouselIndex, slides: carouselSlides, onClose: handleCarouselClose, onIndexChange: setCarouselIndex })));
}
